/**
 * The quick-chat phrase book: every word a cat can ever say, compiled into
 * both the client and the server.
 *
 * THE TABLE IS THE SAFETY MODEL.  Chat in a game aimed at a kid is usually a
 * moderation problem, a filter arms race, and a privacy question all at once
 * -- unless free text simply cannot exist.  Here it cannot: the wire carries
 * an INDEX into this table (see the `say` case in protocol.js) and nothing
 * else, so the complete set of things any player can ever say to any other
 * player is the list below, reviewed once, at authoring time.  There is no
 * filter because there is nothing to filter; a hostile client that sends
 * anything but a small integer gets silence.  This is the same
 * safe-by-construction posture the pose clamp and the look normalizer take:
 * refuse at the edge, never sanitise downstream.
 *
 * TWO STRUCTURAL RULES, both load-bearing:
 *
 *   - THE FLAT ORDER IS APPEND-ONLY FOREVER, exactly like STATES in
 *     constants.js: an index is a promise to every client already running,
 *     and reordering (or deleting) would silently put new words in old cats'
 *     mouths mid-session.  New phrases go at the END of a group's list only
 *     if the group is the LAST group -- anywhere else they renumber
 *     everything after them.  In practice: grow the table by appending whole
 *     new groups, or by appending to "bye".  A client one deploy behind that
 *     receives an index past its own table drops the bubble (phraseText
 *     answers null); a server one deploy behind refuses the index at the
 *     parser.  Either skew degrades to a phrase not shown, never to the
 *     wrong phrase shown.
 *
 *   - EVERY GROUP HOLDS AT MOST NINE PHRASES and there are at most nine
 *     groups, because the whole menu is driven by the digit keys: one digit
 *     picks a group, one digit picks a phrase, and 0 is "back".  A tenth
 *     phrase would need a second keystroke and the menu would stop being
 *     fast, which is the entire reason it is not a chat box.  The test suite
 *     pins both caps, plus a 32-character ceiling per phrase so a bubble
 *     never grows past one comfortable line-wrap over a cat's head.
 *
 * The voice: this is a street of cats and the speaker IS the cat, so
 * everything is written in cat first person -- warm, small, and playable.
 * "Feed me" and "the sun spot is MINE" are jokes a cat gets to make;
 * nothing here can be aimed at a person, which is the other half of safe by
 * construction: a phrase book with no insults in it cannot be used to
 * insult, however creatively it is cut up.
 *
 * No imports, like constants.js: this file sits at the bottom of the
 * dependency graph so both parsers and both UIs can reach it freely.
 */
export const PHRASE_GROUPS = [
    {
        id: "hello",
        label: "hello",
        phrases: [
            "Hi!",
            "Hello!",
            "Over here!",
            "It's me!",
            "Welcome to the street!",
            "New friend!",
            "Mrrow?",
        ],
    },
    {
        id: "wow",
        label: "wow",
        phrases: [
            "Wow!",
            "Did you see that pounce?",
            "That was amazing!",
            "So close!",
            "Hooray!",
            "Oops.",
            "Hee hee!",
            "ZOOMIES!",
        ],
    },
    {
        id: "catlife",
        label: "cat life",
        phrases: [
            "Feed me.",
            "The sun spot is MINE.",
            "Nap time.",
            "This fence is excellent.",
            "Butterflies!!",
            "The creek is cold cold cold!",
            "I meant to do that.",
            "Purrrrrr.",
        ],
    },
    {
        id: "together",
        label: "together",
        phrases: [
            "Follow me!",
            "Let's explore!",
            "Race you to the creek!",
            "Come see this!",
            "Wait for me!",
            "Help me up!",
            "Let's nap in the sun.",
            "You go first.",
        ],
    },
    {
        id: "bye",
        label: "bye",
        phrases: [
            "Bye bye!",
            "See you tomorrow!",
            "Off for a nap.",
            "My human is calling.",
            "Good night, street.",
            "That was fun!",
        ],
    },
];
/** The wire's view: one flat, append-only array.  Derived from the groups so
    the menu and the wire can never disagree about what index 17 says. */
export const PHRASES = [];
for (const g of PHRASE_GROUPS)
    for (const p of g.phrases)
        PHRASES.push(p);
export const PHRASE_COUNT = PHRASES.length;
/**
 * The phrase an index names, or null -- total over `unknown`, in the house
 * parser style: a fractional number, a string, a negative, or an index from
 * a build newer than this one all read as "nothing to say", never as a
 * throw and never as `undefined` handed to a renderer.
 */
export function phraseText(i) {
    return typeof i === "number" && Number.isInteger(i) && i >= 0 && i < PHRASES.length
        ? PHRASES[i]
        : null;
}
